import _ from 'underscore';
import { EDM } from '@bingads-webui/edm-core';
import { resIdsPlugin } from '@bingads-webui/edm-resource-identifiers';
import { odataPlugin } from '@bingads-webui/edm-odata';
import type {
  IEdm,
  ODataOptions,
} from '@bingads-webui/edm-odata';
import { odataBulkUpsertPlugin } from '@bingads-webui/edm-bulk';
import type {
  BulkRequestOptions,
  BulkUpsertEdm,
} from '@bingads-webui/edm-bulk';
import { defineConstProperty } from '@bingads-webui/reflection';
import { schemas } from '@bingads-webui/campaign-odata-schemas';
import { dependants } from '@bingads-webui/campaign-entity-dependants';
import { PerfMarkerFactory } from '@bingads-webui/performance-marker';
import * as bulkUpsertSchema from '@bingads-webui/odata-bulk-upsert-config';
import type { CampaignEdm } from '@bingads-webui/campaign-odata-schemas/types/edm.d';

interface CampaignODataConfigs {
  Token?: string;
  ServiceRoot?: string;
}

type CampaignODataWindow = Window & {
  isDelayedEdmSetup?: boolean;
  campaignODataConfigs?: CampaignODataConfigs;
};

interface InternalRootType {
  ResourceIdentifier: new () => {
    Customers: CampaignEdm['Customers'];
  };
}

// eslint-disable-next-line no-unused-vars
type ResolveRoot = (...args: ['$ROOT']) => InternalRootType;

type RuntimeEdm = IEdm & CampaignEdm & BulkUpsertEdm & Record<string, unknown>;

let marker;
try {
  marker = PerfMarkerFactory.createMarker('CampaignUI_EDM');
} catch (ex) {
  // do nothing
}

if (marker) {
  marker.willInit();
}

const edmInternal = new EDM({
  schemas: {
    $ROOT: {
      $$ref: '$ROOT',
      type: 'object',
      properties: {
        Customers: {
          type: 'array',
          items: schemas['Model/Customer'],
        },
      },
      $$ODataExtension: {
        Name: '$ROOT',
        NavigationProperty: [
          'Customers',
        ],
      },
    },
    ..._.mapObject(
      schemas,
      (schema: unknown, name: string) => _.extend(schema as object, { $$ref: name })
    ),
  },
}) as unknown as RuntimeEdm;

const edm: CampaignEdm = edmInternal;

const edmSetup = () => {
  resIdsPlugin(edmInternal);

  // if the name contains ".", it means it already has a namespace
  // in this case we don't prepend it with the default namespace: "Model"
  const PrefixNamespace = (name: string) => (name.indexOf('.') > 0 ? name : `Model.${name}`);
  const entityDeps: Record<string, string[]> = {};
  _.each(dependants, (list, key) => {
    entityDeps[PrefixNamespace(key)] = _.map(list, (name: string) => PrefixNamespace(name));
  });

  // We call "service root" for the root URL of the service
  // http://docs.oasis-open.org/odata/odata/v4.01/cs01/part1-protocol/odata-v4.01-cs01-part1-protocol.html#sec_ServiceDocumentRequest
  odataPlugin(edmInternal, (window as CampaignODataWindow).campaignODataConfigs?.ServiceRoot || '', entityDeps, {
    processOptions: (options: ODataOptions = {}) => _.extend({}, options, {
      headers: _.extend({
        Authorization: `CCMTSmallToken ${(window as CampaignODataWindow).campaignODataConfigs?.Token || ''}`,
        'x-ms-lastwritetime': 'Max', // TODO: this will make every call bypass MT cache. We should think of a smart way to pass value for it.
      }, options.headers as Record<string, unknown> | undefined),
    }),
    processResponse: (response, resource) => {
      const collectionResponse = response as { value?: unknown[] } | null | undefined;
      // The server should return single Entity
      // but instead it returns same structure as collection call
      // it does return the single Entity as expected with selection clause
      // for patch, it returns empty response with success
      if (resource.type.name === 'Model.Account' && collectionResponse?.value) {
        return collectionResponse.value[0];
      }
      return response;
    },
  });

  odataBulkUpsertPlugin(edmInternal, _.extend({}, (window as CampaignODataWindow).campaignODataConfigs, {
    bulkUpsertSchema,
  }), {
    processOptions: (options: BulkRequestOptions) => _.extend({}, options, {
      headers: _.extend({
        Authorization: `CCMTSmallToken ${(window as CampaignODataWindow).campaignODataConfigs?.Token || ''}`,
      }, options.headers),
    }),
  });

  const root = edmInternal.types.resolve('$ROOT') as unknown as InternalRootType;

  const rootResourceIdentifier = new root.ResourceIdentifier();
  defineConstProperty(edmInternal, 'root', rootResourceIdentifier);
  defineConstProperty(edmInternal, 'Customers', rootResourceIdentifier.Customers);
};

if (!(window as CampaignODataWindow).isDelayedEdmSetup) {
  edmSetup();
}

if (marker) {
  marker.done();
}

export { edm as edmCampaign };
export { edmSetup as edmCampaignSetup };
export type { CampaignEdm };
